import { LoggingService } from '@app/core/services/logging/logging.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AvailableUserVerificationChannelsModel,
  BookingModel,
  OtpChannel,
  PropertyAccessCodesModel,
} from '@app/core/models';
import { BookingRepository } from '@app/store/booking.repository';
import { trackRequestResult } from '@ngneat/elf-requests';
import { from, Observable, switchMap, tap } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { environment } from 'src/environments/environment';
import { FirebaseAuthenticationService } from '../firebase/firebase-authentication/firebase-authentication.service';
import { RouterService } from '../router/router.service';

/** sessionStorage key used to resume the QR-entry flow after login. */
export const PROPERTY_ENTRY_PENDING_KEY = 'propertyEntryPending';

/**
 * Service responsible for handling bookings functionality.
 */
@Injectable()
export class BookingService {
  constructor(
    private _http: HttpClient,
    private _bookingRepo: BookingRepository,
    private _routerService: RouterService,
    private authService: FirebaseAuthenticationService,
  ) {}

  /**
   * Retrieves the user roles from the server.
   * @returns An observable that emits the user roles.
   */
  getAll(): Observable<BookingModel[]> {
    // Correctly await the token before making the request
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        // Use the Headers constructor correctly and include the token
        const headers = new Headers({
          Authorization: `Bearer ${data.token}`,
        });

        return fromFetch<BookingModel[]>(
          `${environment.api.serverUrl}/bookings`,
          {
            headers: headers,
            selector: (response) => response.json(),
          },
        );
      }),
      tap((bookings) => {
        this._bookingRepo.updateBookings(bookings);
        this.checkStoredData().catch((error) => {
          LoggingService.error('Error checking stored data:', error);
        });
      }),
      trackRequestResult(['bookings']),
    );
  }

  getBookingInvitation(bookingId: string): Observable<BookingModel> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.get<BookingModel>(
          `${environment.api.serverUrl}/bookings/${bookingId}/check-invitation`,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  async checkStoredData(): Promise<void> {
    // Resume manual booking form that was interrupted by login
    const storedData = sessionStorage.getItem('bookingFormData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      await this._routerService.navigateToNewBookingPage(
        parsedData.propertyCode,
      );
    }

    // Resume QR-entry verification flow that was interrupted by login
    const entryPending = sessionStorage.getItem(PROPERTY_ENTRY_PENDING_KEY);
    if (entryPending) {
      const { shortCode } = JSON.parse(entryPending);
      if (shortCode) {
        await this._routerService.navigateToPropertyEntryPage(shortCode);
      }
    }
  }

  /**
   * Resolves/attaches the booking identified by an integration key + external
   * booking id (idempotent — creates on first call, re-attaches on repeat
   * calls). Used by `SyncGuard` and by the check-in entry guard to turn
   * PMS-only identifiers (`int`/`extid`) into the internal booking, without a
   * second create: the resolved booking is upserted into `BookingRepository`
   * and marked active here, so callers can read it back instead of syncing
   * again.
   */
  sync(integrationKey: string, bookingId: string): Observable<BookingModel> {
    LoggingService.log('sync');
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<BookingModel>(
          `${environment.api.serverUrl}/bookings/integration/${integrationKey}/booking/${bookingId}`,
          null,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
      tap((booking) => {
        this._bookingRepo.upsertBooking(booking);
        this._bookingRepo.setActiveBookingId(booking._id);
      }),
    );
  }

  getAvailableUserVerificationChannels(
    bookingId: string,
  ): Observable<AvailableUserVerificationChannelsModel> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.get<AvailableUserVerificationChannelsModel>(
          `${environment.api.serverUrl}/bookings/${bookingId}/available-user-verification-channels`,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  verifyGuest(params: {
    bookingId: string;
    otpChannel: OtpChannel;
    phoneId?: string;
    emailId?: string;
    guestVerificationId?: string;
  }): Observable<BookingModel> {
    const body = {
      otpChannel: params.otpChannel,
      phoneId: params.phoneId,
      emailId: params.emailId,
    };
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<BookingModel>(
          `${environment.api.serverUrl}/bookings/${params.bookingId}/verify-guest`,
          body,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  confirmGuestOtp(
    bookingId: string,
    code: string,
    guestVerificationId?: string,
  ): Observable<BookingModel> {
    const body = {
      code,
      guestVerificationId,
    };
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<BookingModel>(
          `${environment.api.serverUrl}/bookings/${bookingId}/confirm-guest-otp`,
          body,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
      tap((booking) => this._bookingRepo.upsertBooking(booking)),
    );
  }

  getPropertyAccessCode(
    bookingId: string,
  ): Observable<PropertyAccessCodesModel> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.get<PropertyAccessCodesModel>(
          `${environment.api.serverUrl}/bookings/${bookingId}/property-access-code`,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  unlockSmartLock(bookingId: string) {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post(
          `${environment.api.serverUrl}/bookings/${bookingId}/unlock-smart-lock`,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  // ─── Property-entry / QR attach flow ─────────────────────────────────────

  /**
   * Sends an OTP to the phone/email of the candidate booking identified by
   * the verification token.
   */
  attachSendOtp(
    bookingVerificationToken: string,
    channel: OtpChannel,
  ): Observable<{ success: boolean }> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) =>
        this._http.post<{ success: boolean }>(
          `${environment.api.serverUrl}/bookings/attach/send-otp`,
          { bookingVerificationToken, channel },
          { headers: { Authorization: `Bearer ${data.token}` } },
        ),
      ),
    );
  }

  /**
   * Confirms the OTP and attaches the authenticated user to the booking.
   * On success the booking is returned and upserted into the local store.
   */
  attachConfirmOtp(
    bookingVerificationToken: string,
    code: string,
  ): Observable<BookingModel> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) =>
        this._http.post<BookingModel>(
          `${environment.api.serverUrl}/bookings/attach/confirm-otp`,
          { bookingVerificationToken, code },
          { headers: { Authorization: `Bearer ${data.token}` } },
        ),
      ),
      tap((booking) => {
        this._bookingRepo.upsertBooking(booking);
        this._bookingRepo.setActiveBookingId(booking._id);
      }),
    );
  }

  resendGuestCodeVerification(params: {
    bookingId: string;
    guestVerificationId: string;
  }): Observable<BookingModel> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<BookingModel>(
          `${environment.api.serverUrl}/bookings/${params.bookingId}/guest-verifications/${params.guestVerificationId}/resend-code`,
          null,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  guestIsValidatedInBooking(
    bookingId: string,
    userFirebaseId: string,
  ): Observable<boolean> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.get<boolean>(
          `${environment.api.serverUrl}/bookings/${bookingId}/guest-validated/${userFirebaseId}`,
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }
}
